import React, { useState, useEffect } from "react";
import { Box, Button, Group, Title, Text, Container, Paper, Center, Stack } from "@mantine/core";
import { IconKey, IconDownload, IconPlus } from "@tabler/icons-react";
import { useLiveQuery } from "dexie-react-hooks";
import { useNavigate } from "@tanstack/react-location";
import { nanoid } from "nanoid";
import { SettingsModal } from "../components/SettingsModal";
import { db } from "../db";
import { config } from "../utils/config";
import { ModelSelector, WritingOptionsSelector } from "../components/dynamic";

// Define interface for the install prompt event
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: Array<string>;
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed',
    platform: string
  }>;
  prompt(): Promise<void>;
}

export function IndexRoute() {
  const settings = useLiveQuery(async () => {
    return db.settings.where({ id: "general" }).first();
  });

  // Check if the current provider has an API key set
  const apiKeyExists = !!(settings?.provider && 
                          settings?.providerSettings && 
                          settings?.providerSettings[settings.provider] && 
                          settings?.providerSettings[settings.provider].apiKey);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // State to hold the install prompt event
  const [installPromptEvent, setInstallPromptEvent] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      console.log("beforeinstallprompt event caught");
      setInstallPromptEvent(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []); // Empty array means this effect runs once on mount

  const handleInstallClick = async () => {
    if (!installPromptEvent) {
      console.log("Install prompt event not available");
      return;
    }
    installPromptEvent.prompt();
    const { outcome } = await installPromptEvent.userChoice;
    console.log(`User response to the install prompt: ${outcome}`);
    setInstallPromptEvent(null);
  };

  const navigate = useNavigate();
  
  // Create a new chat and navigate to it
  const createNewChat = async () => {
    const id = nanoid();
    await db.chats.add({
      id,
      description: "New Chat",
      totalTokens: 0,
      createdAt: new Date(),
      pinned: false,
    });
    navigate({ to: `/chats/${id}` });
  };

  return (
    <Box sx={{ height: "100%" }}>
      <Container size="md" py="xl">
        <Stack spacing="xl">
          <Center>
            <Title order={1} align="center" mb="md" color="grey">Welcome to Chatpad</Title>
          </Center>
          
          <Stack spacing="xs">
          <Text align="center" size="lg" color = "dark-grey">
            A dynamic chat application that supports multiple AI providers
          </Text>
          <Text align="center" size="xs" color = "dark-grey">
            A heavily customized clone of Chatpad AI
          </Text>
          </Stack>
          
          <Paper p="xl" withBorder shadow="md" radius="md">
            <Stack spacing="md">
              <Title order={3} align="center" mb="sm" color="grey">Start a New Conversation</Title>
              
              <ModelSelector />
              
              <WritingOptionsSelector />
              
              <Button
                size="lg"
                fullWidth
                leftIcon={<IconPlus size={20} />}
                onClick={createNewChat}
              >
                New Chat
              </Button>
            </Stack>
          </Paper>
          
          <Group position="center" mt="md">
            {config.allowSettingsModal && (
              <SettingsModal>
                <Button
                  variant={apiKeyExists ? "subtle" : "outline"}
                  leftIcon={<IconKey size={20} />}
                >
                  {apiKeyExists ? "API Settings" : "Enter API Key"}
                </Button>
              </SettingsModal>
            )}
            
            {installPromptEvent && (
              <Button
                onClick={handleInstallClick}
                variant="subtle"
                leftIcon={<IconDownload size={20} />}
              >
                Install App
              </Button>
            )}
          </Group>
        </Stack>
      </Container>
    </Box>
  );
}
